// Shared helpers for the KPI dashboard and the alert bell: how to show a value, what each status means, and which KPIs each
// role's view leads with. The server decides what a person may see; these only arrange it.
import { AlertTriangle, CheckCircle2, MinusCircle, OctagonAlert } from "lucide-react";

export const STATUS = {
  RED: { label: "Act today", short: "Red", icon: OctagonAlert, chip: "bg-red-50 text-red-700 ring-red-200 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/30", bar: "bg-red-600" },
  AMBER: { label: "Watch", short: "Amber", icon: AlertTriangle, chip: "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30", bar: "bg-amber-500" },
  GREEN: { label: "On track", short: "Green", icon: CheckCircle2, chip: "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30", bar: "bg-emerald-600" },
  NO_DATA: { label: "No data", short: "No data", icon: MinusCircle, chip: "bg-slate-100 text-slate-500 ring-slate-200 dark:bg-slate-500/10 dark:text-slate-400 dark:ring-slate-500/30", bar: "bg-slate-300" },
};
export const statusOf = (s) => STATUS[s] || STATUS.NO_DATA;
export const STATUS_ORDER = { RED: 0, AMBER: 1, GREEN: 2, NO_DATA: 3 };

const inrFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

/** ₹12.4 L / ₹1.2 Cr for big amounts, so a card stays readable. */
export function inrShort(v) {
  const n = Number(v);
  const a = Math.abs(n);
  if (a >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `₹${(n / 1e5).toFixed(1)} L`;
  return `₹${inrFmt.format(n)}`;
}

export function formatKpi(value, unit) {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (unit === "INR") return inrShort(n);
  if (unit === "ratio") return n.toFixed(2);
  if (unit === "days") return `${Math.round(n * 10) / 10} d`;
  if (unit === "%") return `${n.toFixed(1)}%`;
  return String(n);
}

/** "Amber below 1.00 · Red below 0.90" from the definition. */
export function thresholdText(row) {
  const word = row.direction === "lower_is_better" ? "above" : "below";
  const parts = [];
  if (row.amber_threshold !== null && row.amber_threshold !== undefined) parts.push(`Amber ${word} ${formatKpi(row.amber_threshold, row.unit)}`);
  if (row.red_threshold !== null && row.red_threshold !== undefined) parts.push(`Red ${word} ${formatKpi(row.red_threshold, row.unit)}`);
  return parts.join(" · ") || "No limits set";
}

/** Did the change go the good way? Used to word the trend, not to colour it. */
export function trendIsGood(row) {
  if (!row.trend || row.trend === "FLAT") return null;
  const up = row.trend === "UP";
  return row.direction === "lower_is_better" ? !up : up;
}

export const VIEWS = {
  management: { label: "Management", codes: null, hint: "Every project and company at a glance." },
  project: { label: "Project manager", codes: ["PHYSICAL_PROGRESS", "SPI", "CPI", "DELAY_DAYS", "BUDGET_VARIANCE_PCT", "FORECAST_MARGIN_PCT", "WO_UTILISATION_PCT", "MACHINE_UTILISATION_PCT", "INDENT_TO_PO_DAYS"], hint: "One project: schedule, cost and site resources." },
  stores: { label: "Stores & purchasing", codes: ["DEAD_STOCK_VALUE", "INDENT_TO_PO_DAYS", "PRICE_VARIANCE_PCT", "MACHINE_UTILISATION_PCT"], hint: "Stock, buying speed and prices." },
  accounts: { label: "Accounts", codes: ["RECEIVABLES_OVERDUE", "GROSS_MARGIN_PCT", "FORECAST_MARGIN_PCT", "BUDGET_VARIANCE_PCT", "CPI", "BILLING_LAG_DAYS"], hint: "Money in, margins and billing." },
};

export function defaultView(role) {
  if (role === "ORG_ADMIN" || role === "PORTFOLIO_LEAD") return "management";
  if (role === "ACCOUNTS") return "accounts";
  if (role === "STORES" || role === "PROCUREMENT") return "stores";
  return "project";
}

/** The columns of the management table: one headline KPI per concern. */
export const MATRIX_CODES = ["PHYSICAL_PROGRESS", "SPI", "CPI", "DELAY_DAYS", "BUDGET_VARIANCE_PCT", "FORECAST_MARGIN_PCT"];
